import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

import Button from 'shopper/components/Button';
import Icon from 'shopper/components/Icon';

import useExclusiveOffer from 'hooks/useExclusiveOffer';
import useModal from 'hooks/useModal';

import { sendEvent } from 'lib/gtag';
import { toOfferRedirectLink } from 'lib/links';
import { getOfferPageEventCategory } from 'lib/offer';
import placeholder from 'lib/placeholder';

const OfferWithCouponModal = dynamic(() => import('./OfferWithCouponModal'), {
  ssr: false,
});

const GoToStoreButton = ({
  gaEventCategoryPostfix = null,
  offerCta = null,
  offerCoupon = null,
  offerId,
  offerStatusName,
  offerUserVisibility,
  type = 'success',
  utms = null,
  ...rest
}) => {
  const { showModal } = useModal();
  const { isOfferBlocked } = useExclusiveOffer();

  const sharedProps = {
    children: offerCta ?? placeholder('actions.goToStore'),
    disabled: isOfferBlocked(offerUserVisibility),
    iconRight: <Icon name="link-blank" />,
    full: true,
    type,
  };

  const gaEventCategory = `${getOfferPageEventCategory(offerStatusName)}${
    gaEventCategoryPostfix ? `_${gaEventCategoryPostfix}` : ''
  }`;

  if (offerCoupon) {
    return (
      <Button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();

          sendEvent({
            action: 'modal_coupon_open',
            category: 'active_offer_buy',
            label: offerCta ?? placeholder('actions.goToStore'),
          });
          showModal(OfferWithCouponModal, {
            offerCta,
            offerCoupon,
            offerId,
            gaEventCategoryPostfix,
          });
        }}
        {...sharedProps}
        {...rest}
      />
    );
  }

  return (
    <Button
      as="a"
      href={toOfferRedirectLink(offerId, utms)}
      target="_blank"
      onClick={(e) => {
        e.stopPropagation();

        sendEvent({
          action: 'go_store',
          category: gaEventCategory,
          label: offerCta ?? placeholder('actions.goToStore'),
        });
      }}
      {...sharedProps}
      {...rest}
    />
  );
};

GoToStoreButton.displayName = 'GoToStoreButton';

GoToStoreButton.propTypes = {
  gaEventCategoryPostfix: PropTypes.string,
  offerCta: PropTypes.string,
  offerCoupon: PropTypes.string,
  offerId: PropTypes.number.isRequired,
  offerStatusName: PropTypes.string.isRequired,
  offerUserVisibility: PropTypes.string.isRequired,
  size: PropTypes.string,
  type: PropTypes.string,
  utms: PropTypes.string,
};

export default GoToStoreButton;
